import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Category, Problematique } from '../types';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiPriceTag3Line,
  RiCloseLine, RiCheckLine,
} from 'react-icons/ri';

const inputClass =
  'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const AdminCatalog: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCatName, setNewCatName] = useState('');
  const [problematiques, setProblematiques] = useState<Problematique[]>([]);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const [services, setServices] = useState<any[]>([]);
  const [newService, setNewService] = useState({
    code: '', nom: '', categorie_id: 0, prix: 0, duree_min: 0,
    description: '', problematique_couverte: '', video_url: '', bienfaits_principaux: '',
  });
  const [editingService, setEditingService] = useState<any | null>(null);

  useEffect(() => {
    fetchCategories();
    fetchServices();
    fetchProblematiques();
  }, []);

  const fetchProblematiques = async () => {
    const res = await api.get('/problematiques/');
    setProblematiques(res.data);
  };

  const fetchCategories = async () => {
    const res = await api.get('/categories/');
    setCategories(res.data);
    if (res.data.length > 0) {
      setNewService(s => ({ ...s, categorie_id: res.data[0].id }));
    }
  };

  const fetchServices = async () => {
    const res = await api.get('/services/');
    setServices(res.data);
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      await api.put(`/categories/${editingCategory.id}`, { nom: newCatName });
      setEditingCategory(null);
    } else {
      await api.post('/categories/', { nom: newCatName });
    }
    setNewCatName('');
    fetchCategories();
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm('Supprimer cette catégorie ?')) {
      await api.delete(`/categories/${id}`);
      fetchCategories();
    }
  };

  const editCategory = (category: Category) => {
    setEditingCategory(category);
    setNewCatName(category.nom);
  };

  const cancelCatEdit = () => {
    setEditingCategory(null);
    setNewCatName('');
  };

  const handleAddService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingService) {
      await api.put(`/services/${editingService.id}`, newService);
      setEditingService(null);
    } else {
      await api.post('/services/', newService);
    }
    setNewService({
      code: '', nom: '',
      categorie_id: categories.length > 0 ? categories[0].id : 0,
      prix: 0, duree_min: 0, description: '', problematique_couverte: '',
      video_url: '', bienfaits_principaux: '',
    });
    fetchServices();
  };

  const handleDeleteService = async (id: number) => {
    if (window.confirm('Supprimer ce service ?')) {
      await api.delete(`/services/${id}`);
      fetchServices();
    }
  };

  const editService = (service: any) => {
    setEditingService(service);
    setNewService({
      code: service.code,
      nom: service.nom,
      categorie_id: service.categorie_id,
      prix: service.prix,
      duree_min: service.duree_min,
      description: service.description || '',
      problematique_couverte: service.problematique_couverte || '',
      video_url: service.video_url || '',
      bienfaits_principaux: service.bienfaits_principaux || '',
    });
  };

  const cancelServiceEdit = () => {
    setEditingService(null);
    setNewService({
      code: '', nom: '',
      categorie_id: categories.length > 0 ? categories[0].id : 0,
      prix: 0, duree_min: 0, description: '', problematique_couverte: '',
      video_url: '', bienfaits_principaux: '',
    });
  };

  const getCatName = (id: number) => categories.find(c => c.id === id)?.nom ?? '—';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Catalogue</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez les catégories et les services proposés</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ── Categories column ── */}
        <div className="xl:col-span-1 space-y-5">
          {/* Form */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <RiPriceTag3Line className="text-brand-500" />
              {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </h2>
            <form onSubmit={handleAddCategory} className="space-y-3">
              <div>
                <label className={labelClass}>Nom</label>
                <input
                  type="text"
                  placeholder="ex: Soin hydratant"
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium py-2 rounded-lg transition"
                >
                  <RiCheckLine />
                  {editingCategory ? 'Modifier' : 'Ajouter'}
                </button>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={cancelCatEdit}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 text-sm transition"
                  >
                    <RiCloseLine />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* List */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">
                Catégories
                <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {categories.length}
                </span>
              </h3>
            </div>
            {categories.length === 0 ? (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">Aucune catégorie</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {categories.map(c => (
                  <li key={c.id} className="flex items-center justify-between px-5 py-3">
                    <span className="text-sm text-gray-800">{c.nom}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => editCategory(c)}
                        className="p-1.5 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition"
                      >
                        <RiEditLine />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(c.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      >
                        <RiDeleteBinLine />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* ── Services column ── */}
        <div className="xl:col-span-2 space-y-5">
          {/* Form */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <RiAddLine className="text-brand-500" />
              {editingService ? 'Modifier le service' : 'Nouveau service'}
            </h2>
            <form onSubmit={handleAddService} className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Code</label>
                <input type="text" placeholder="ex: SH01" value={newService.code}
                  onChange={e => setNewService({ ...newService, code: e.target.value })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Nom du service</label>
                <input type="text" placeholder="ex: Shampoing traitant" value={newService.nom}
                  onChange={e => setNewService({ ...newService, nom: e.target.value })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Catégorie</label>
                <select value={newService.categorie_id}
                  onChange={e => setNewService({ ...newService, categorie_id: parseInt(e.target.value) })}
                  className={inputClass}>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Problématique couverte</label>
                <select value={newService.problematique_couverte || ''}
                  onChange={e => setNewService({ ...newService, problematique_couverte: e.target.value })}
                  className={inputClass}>
                  <option value="">Aucune problématique</option>
                  {problematiques.map(p => <option key={p.nom} value={p.nom}>{p.nom}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Prix (F CFA)</label>
                <input type="number" min="0" placeholder="0" value={newService.prix || ''}
                  onChange={e => setNewService({ ...newService, prix: parseFloat(e.target.value) })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Durée (min)</label>
                <input type="number" min="0" placeholder="0" value={newService.duree_min || ''}
                  onChange={e => setNewService({ ...newService, duree_min: parseInt(e.target.value) })}
                  className={inputClass} required />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Description</label>
                <textarea placeholder="Description courte du service" value={newService.description}
                  onChange={e => setNewService({ ...newService, description: e.target.value })}
                  className={inputClass} rows={3} />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Bienfaits Principaux</label>
                <input type="text" placeholder="Bienfaits du service" value={newService.bienfaits_principaux}
                  onChange={e => setNewService({ ...newService, bienfaits_principaux: e.target.value })}
                  className={inputClass} />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Vidéo URL</label>
                <input type="text" placeholder="URL de la vidéo" value={newService.video_url}
                  onChange={e => setNewService({ ...newService, video_url: e.target.value })}
                  className={inputClass} />
              </div>
              <div className="col-span-2 flex gap-2 pt-1">
                <button type="submit"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium py-2.5 rounded-lg transition">
                  <RiCheckLine />
                  {editingService ? 'Enregistrer les modifications' : 'Ajouter le service'}
                </button>
                {editingService && (
                  <button type="button" onClick={cancelServiceEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 text-sm transition">
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Services table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">
                Services
                <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {services.length}
                </span>
              </h3>
            </div>
            {services.length === 0 ? (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">Aucun service</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Service</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Catégorie</th>
                      <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Prix</th>
                      <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Durée</th>
                      <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {services.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3">
                          <p className="font-medium text-gray-900">{s.nom}</p>
                          <p className="text-xs text-gray-400">{s.code}</p>
                        </td>
                        <td className="px-5 py-3 text-gray-600">{getCatName(s.categorie_id)}</td>
                        <td className="px-5 py-3 text-right text-gray-800 font-medium">
                          {s.prix?.toLocaleString()} F
                        </td>
                        <td className="px-5 py-3 text-right text-gray-500">{s.duree_min} min</td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => editService(s)}
                              className="p-1.5 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition">
                              <RiEditLine />
                            </button>
                            <button onClick={() => handleDeleteService(s.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
                              <RiDeleteBinLine />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCatalog;
